import { useEffect, useState } from "react";
import { LoadingDiv, SweetAlertShow } from "../master/Master";
import { allStudentFeeReport } from "../../api/Student";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { Link, useLocation, useNavigate } from "react-router-dom";
import usePrivateLink from "../config/usePrivateLink";
import DownloadExcel from "../master/DownloadExcel";
import { PrintPage } from "../master/Print";

const num = (v) => Number(v || 0);
const inr = (n) =>
  "₹ " + num(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const MODULE_BADGE_COLOR = {
  "Batch Fee": "primary",
  "Hostel Fee": "success",
  "Transport Fee": "warning",
  "Library Fee": "info",
};
const MODULE_DEFS = [
  {
    key: "student_batch_n_fees",
    title: "Batch Fee",
    icon: "ri-graduation-cap-line",
    color: "primary",
    fields: {
      total: "total_fee",
      discount: "total_discount",
      final: "final_fee",
      paid: "total_paid",
      due: "total_due",
    },
  },
  {
    key: "student_hostel_n_fees",
    title: "Hostel Fee",
    icon: "ri-hotel-bed-line",
    color: "success",
    fields: {
      total: "total_amount",
      final: "final_amount",
      paid: "paid_amount",
      due: "left_amount",
    },
  },
  {
    key: "student_transport_n_fees",
    title: "Transport Fee",
    icon: "ri-bus-line",
    color: "warning",
    fields: {
      total: "total_fee",
      discount: "discount",
      final: "final_fee",
      paid: "total_paid",
      due: "total_due",
    },
  },
  {
    key: "student_library_n_fees",
    title: "Library Fee",
    icon: "ri-book-2-line",
    color: "info",
    fields: {
      total: "total_amount",
      final: "final_amount",
      paid: "paid_amount",
      due: "left_amount",
    },
  },
];

function aggregateModule(students, key, fields) {
  const studentIds = new Set();
  const totals = { total: 0, discount: 0, final: 0, paid: 0, due: 0 };

  students.forEach((s) => {
    const records = s?.[key] || [];
    if (records.length > 0) studentIds.add(s.id);
    records.forEach((r) => {
      const total = num(r[fields.total]);
      const final = num(r[fields.final]);
      const paid = num(r[fields.paid]);
      totals.total += total;
      totals.discount += fields.discount
        ? num(r[fields.discount])
        : total - final;
      totals.final += final;
      totals.paid += paid;
      totals.due += fields.due ? num(r[fields.due]) : final - paid;
    });
  });

  return { students: studentIds.size, ...totals };
}

function calculateStudentTotals(student) {
  const perModule = MODULE_DEFS.map((m) => {
    const records = student?.[m.key] || [];
    const t = records.reduce(
      (acc, r) => {
        const total = num(r[m.fields.total]);
        const final = num(r[m.fields.final]);
        const paid = num(r[m.fields.paid]);
        return {
          total: acc.total + total,
          discount:
            acc.discount +
            (m.fields.discount ? num(r[m.fields.discount]) : total - final),
          final: acc.final + final,
          paid: acc.paid + paid,
          due: acc.due + (m.fields.due ? num(r[m.fields.due]) : final - paid),
        };
      },
      { total: 0, discount: 0, final: 0, paid: 0, due: 0 },
    );
    return { title: m.title, count: records.length, ...t };
  });

  const sum = (key) => perModule.reduce((a, m) => a + m[key], 0);
  return {
    totalFee: sum("total"),
    totalDiscount: sum("discount"),
    finalFee: sum("final"),
    totalPaid: sum("paid"),
    totalDue: sum("due"),
    breakdown: perModule.filter((m) => m.count > 0),
  };
}

export default function StudentFeeReport() {
  const navigate = useNavigate();
  const hasAccess = usePrivateLink();
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [filters, setFilters] = useState({});
  const [students, setStudents] = useState([]);

  useEffect(() => {
    const parseSessions = JSON.parse(localStorage.getItem("sessions") ?? "[]");
    setSessions(parseSessions);
  }, []);

  const location = useLocation();
  const { batch_id, hostel_id, bus_id, library_id, session_id } =
    location.state || {};

  useEffect(() => {
    console.log("state", location.state);
    if (batch_id && session_id) {
      setFilters((prev) => ({ ...prev, batch_id, session_id: session_id }));
    } else if (hostel_id && session_id) {
      setFilters((prev) => ({ ...prev, hostel_id, session_id: session_id }));
    } else if (bus_id && session_id) {
      setFilters((prev) => ({ ...prev, bus_id, session_id: session_id }));
    } else if (library_id && session_id) {
      setFilters((prev) => ({ ...prev, library_id, session_id: session_id }));
    }
  }, [batch_id, hostel_id, bus_id, library_id, session_id]);

  useEffect(() => {
    const activeSession = sessions?.find((s) => Number(s.status) === 1)?.id;
    setFilters((prev) => {
      if (prev.session_id || !activeSession) return prev;
      return { ...prev, session_id: activeSession };
    });
  }, [sessions]);

  const getStudentDueFeeReport = async () => {
    setLoading(true);
    try {
      let addUrl = "";
      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (value) addUrl += `&${key}=${encodeURIComponent(value)}`;
      });
      const response = await allStudentFeeReport(addUrl);
      if (response?.data?.status) {
        setStudents(response?.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filters.session_id) getStudentDueFeeReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const rows = students.map((s) => ({
    student: s,
    totals: calculateStudentTotals(s),
  }));

  const summary = rows.reduce(
    (acc, r) => ({
      totalFee: acc.totalFee + r.totals.totalFee,
      totalDiscount: acc.totalDiscount + r.totals.totalDiscount,
      finalFee: acc.finalFee + r.totals.finalFee,
      totalPaid: acc.totalPaid + r.totals.totalPaid,
      totalDue: acc.totalDue + r.totals.totalDue,
    }),
    { totalFee: 0, totalDiscount: 0, finalFee: 0, totalPaid: 0, totalDue: 0 },
  );

  const modules = MODULE_DEFS.map((m) => ({
    ...m,
    data: aggregateModule(students, m.key, m.fields),
  }));

  const grand = modules.reduce(
    (acc, m) => ({
      total: acc.total + m.data.total,
      discount: acc.discount + m.data.discount,
      final: acc.final + m.data.final,
      paid: acc.paid + m.data.paid,
      due: acc.due + m.data.due,
    }),
    { total: 0, discount: 0, final: 0, paid: 0, due: 0 },
  );

  const renderBreakdown = (breakdown) =>
    breakdown.map((b, i) => {
      const color = MODULE_BADGE_COLOR[b.title] || "secondary";
      return (
        <span
          key={i}
          className={`badge bg-${color}-subtle text-${color} rounded-pill me-1 mb-1 fw-semibold text-nowrap`}
          style={{ fontSize: "0.7rem" }}
        >
          {b.title} · {inr(b.final)}
        </span>
      );
    });

  const handleDownload = async (type = "excel") => {
    setLoading(true);
    try {
      const downloadData = [
        ...rows.map(({ student, totals }, index) => ({
          "Registration No": student.registration_no,
          "Student Name": student.name,
          "Father Name": student.father_name,
          Phone: student.phone || "",
          "Fee Modules": totals?.breakdown
            ?.map((b) => `${b.title} (Due: ${inr(b.final)})`)
            .join("\n"),
          "Payable Fee": totals.finalFee,
          "Received Fee": totals.totalPaid,
          "Due Fee": totals.totalDue,
        })),

        {
          "Registration No": "",
          "Student Name": "TOTAL",
          "Father Name": "",
          Phone: "",
          "Fee Modules": "",
          "Payable Fee": summary.finalFee,
          "Received Fee": summary.totalPaid,
          "Due Fee": summary.totalDue,
        },
      ];

      if (downloadData.length) {
        if (type === "excel") {
          const now = new Date();

          const fileName = `Report_${now.getFullYear()}_${now.getMonth() + 1
            }_${now.getDate()}_${now.getHours()}_${now.getMinutes()}`;

          DownloadExcel(downloadData, [], fileName, []);
        } else {
          PrintPage(downloadData);
        }
      } else {
        SweetAlertShow("info", "Info", "No records found");
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  {/* <Master /> */}

                  <div className="col-12">
                    <div className="card border-0 shadow-sm rounded-4 p-3 mb-3">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="bg-primary text-white rounded-3 d-flex align-items-center justify-content-center fs-5"
                            style={{ width: 46, height: 46 }}
                          >
                            <i className="ri-money-rupee-circle-line"></i>
                          </div>
                          <div>
                            <h5 className="m-0 fw-bold">
                              Fees Report{" "}
                              {batch_id === "only"
                                ? "Batches"
                                : hostel_id === "only"
                                  ? "Hostels"
                                  : bus_id === "only"
                                    ? "Transport"
                                    : library_id === "only" && "Library"}{" "}
                              Students
                            </h5>
                            <small className="text-muted">
                              Monitor fees collection and analytics
                            </small>
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <div
                            className="form-floating form-floating-outline"
                            style={{ minWidth: "220px" }}
                          >
                            <select
                              className="form-select rounded-3"
                              value={filters?.session_id || ""}
                              onChange={(e) =>
                                setFilters({
                                  ...filters,
                                  session_id: e.target.value,
                                })
                              }
                            >
                              {sessions?.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.session} :{" "}
                                  {s.status === "1" ? "Active" : "Inactive"}
                                </option>
                              ))}
                            </select>
                            <label>Session</label>
                          </div>

                          <div
                            className="form-floating form-floating-outline"
                            style={{ minWidth: "180px" }}
                          >
                            <select
                              className="form-select rounded-3"
                              value={filters?.rte_status || ""}
                              onChange={(e) =>
                                setFilters({
                                  ...filters,
                                  rte_status: e.target.value,
                                })
                              }
                            >
                              <option value="">All</option>
                              <option value="RTE">RTE</option>
                              <option value="NON-RTE">NON-RTE</option>
                            </select>
                            <label>RTE Status</label>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* ===================== Hero Summary ===================== */}
                    <div className="row g-3 mb-4">
                      {/* Students */}
                      <div className="col-md-3">
                        <div className="card border-0 shadow-sm rounded-4 h-100 bg-gradient bg-primary text-white overflow-hidden position-relative">
                          <div
                            className="card-body position-relative"
                            style={{ zIndex: 1 }}
                          >
                            <div className="d-flex align-items-center justify-content-between mb-1">
                              <small
                                className="text-white-50 fw-semibold text-uppercase"
                                style={{ letterSpacing: "0.5px" }}
                              >
                                Total Students{" "}
                                {batch_id === "only"
                                  ? "batches"
                                  : hostel_id === "only"
                                    ? "hostels"
                                    : bus_id === "only"
                                      ? "transport"
                                      : library_id === "only" && "library"}
                              </small>
                              <i className="ri-group-line fs-3 opacity-75"></i>
                            </div>
                            <h2 className="mb-0 fw-bold">{students?.length}</h2>
                            <small className="text-white-50">
                              Unique across all modules
                            </small>
                          </div>
                          <i
                            className="ri-group-fill position-absolute"
                            style={{
                              right: -10,
                              bottom: -20,
                              fontSize: 100,
                              opacity: 0.12,
                            }}
                          ></i>
                        </div>
                      </div>

                      {/* Payable Fee */}
                      <div className="col-md-5">
                        <div className="card border-0 shadow-sm rounded-4 h-100">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h6
                                className="fw-bold mb-0 text-uppercase text-muted"
                                style={{ letterSpacing: "0.5px", fontSize: 12 }}
                              >
                                Payable Fee Breakdown
                              </h6>
                              <div
                                className="bg-primary-subtle text-primary rounded-3 d-flex align-items-center justify-content-center"
                                style={{ width: 42, height: 42 }}
                              >
                                <i className="ri-money-rupee-circle-line fs-5"></i>
                              </div>
                            </div>

                            <div className="d-flex justify-content-between mb-2">
                              <span className="text-muted">Total Fee</span>
                              <strong>{inr(grand.total)}</strong>
                            </div>

                            <div className="d-flex justify-content-between text-danger mb-2">
                              <span>Discount</span>
                              <strong>- {inr(grand.discount)}</strong>
                            </div>

                            <hr className="my-2" />

                            <div className="d-flex justify-content-between align-items-center">
                              <span className="fw-bold">Final Payable</span>
                              <span className="fw-bold text-success fs-4">
                                {inr(grand.final)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Received */}
                      <div
                        className="col-md-2 cursor-pointer"
                        onClick={() => {
                          navigate(`/fees/transactions`);
                        }}
                      >
                        <div className="card border-0 shadow-sm rounded-4 h-100">
                          <div className="card-body text-center d-flex flex-column justify-content-center">
                            <div
                              className="bg-success-subtle text-success rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2"
                              style={{ width: 52, height: 52 }}
                            >
                              <i className="ri-checkbox-circle-line fs-3"></i>
                            </div>
                            <h5 className="mb-0 fw-bold">{inr(grand.paid)}</h5>
                            <small className="text-muted">Received</small>
                          </div>
                        </div>
                      </div>

                      {/* Due */}
                      <div className="col-md-2">
                        <div className="card border-0 shadow-sm rounded-4 h-100">
                          <div className="card-body text-center d-flex flex-column justify-content-center">
                            <div
                              className="bg-warning-subtle text-warning rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2"
                              style={{ width: 52, height: 52 }}
                            >
                              <i className="ri-time-line fs-3"></i>
                            </div>
                            <h5 className="mb-0 fw-bold">{inr(grand.due)}</h5>
                            <small className="text-muted">Due</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ===================== Module Wise ===================== */}
                    <div className="row g-3  mb-4">
                      {modules.map((item) => {
                        console.log(item);
                        const pct = (
                          item.data.final > 0
                            ? (item.data.paid / item.data.final) * 100
                            : 0
                        )?.toFixed(0);
                        const hasData = item?.data?.students > 0;
                        return (
                          <div className="col-xl-3 col-md-6" key={item.key}>
                            <div
                              className={`card border-0 shadow-sm rounded-4 h-100 border-start border-4 border-${item.color} ${!hasData ? "opacity-75" : ""
                                }`}
                            >
                              <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center pb-0">
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    className={`bg-${item.color}-subtle text-${item.color} rounded-3 d-flex align-items-center justify-content-center`}
                                    style={{ width: 38, height: 38 }}
                                  >
                                    <i className={`${item.icon} fs-5`}></i>
                                  </div>
                                  <h6 className="mb-0 fw-bold">{item.title}</h6>
                                </div>
                              </div>

                              <div className="card-body">
                                {hasData ? (
                                  <>
                                    <div className="d-flex justify-content-between mb-2">
                                      <span className="text-muted small">
                                        Students
                                      </span>
                                      <strong>{item?.data?.students}</strong>
                                    </div>

                                    <div className="d-flex justify-content-between mb-2">
                                      <span className="text-muted small">
                                        Total Fee
                                      </span>
                                      <strong>{inr(item?.data?.total)}</strong>
                                    </div>

                                    <div className="d-flex justify-content-between mb-2 text-danger">
                                      <span className="small">Discount</span>
                                      <strong>
                                        - {inr(item?.data?.discount)}
                                      </strong>
                                    </div>

                                    <div className="d-flex justify-content-between mb-2">
                                      <span className="text-muted small">
                                        Payable
                                      </span>
                                      <strong className="text-primary">
                                        {inr(item?.data?.final)}
                                      </strong>
                                    </div>

                                    <div className="d-flex justify-content-between mb-2 text-success">
                                      <span className="small">Received</span>
                                      <strong>{inr(item?.data?.paid)}</strong>
                                    </div>

                                    <div className="d-flex justify-content-between mb-3 text-warning">
                                      <span className="small">Due</span>
                                      <strong>{inr(item?.data?.due)}</strong>
                                    </div>

                                    <div
                                      className="progress rounded-pill"
                                      style={{ height: 6 }}
                                    >
                                      <div
                                        className={`progress-bar bg-${item.color} rounded-pill`}
                                        style={{ width: `${pct}%` }}
                                      />
                                    </div>
                                    <small className="text-muted d-block text-end mt-1">
                                      {pct}% collected
                                    </small>
                                  </>
                                ) : (
                                  <div className="text-center text-muted py-4">
                                    <i className="ri-inbox-line fs-2 d-block mb-2 opacity-50"></i>
                                    <small>No data available</small>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-3">
                      <div className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-3 d-flex align-items-center justify-content-center text-white"
                            style={{
                              width: 44,
                              height: 44,
                              background:
                                "linear-gradient(135deg, #ef4444, #f59e0b)",
                            }}
                          >
                            <i className="ri-file-chart-line fs-5"></i>
                          </div>
                          <div>
                            <h6 className="m-0 fw-bold">
                              Students With Due Fee
                            </h6>
                            <small className="text-muted">
                              Aggregated across batch, hostel, transport &amp;
                              library fees
                            </small>
                          </div>
                        </div>

                        <div className="d-flex gap-2">
                          <span className="badge bg-light text-dark border fw-semibold px-3 py-2 rounded-pill">
                            <i className="ri-list-check me-1"></i>
                            {rows.length} student{rows.length === 1 ? "" : "s"}
                          </span>
                          <button
                            type="button"
                            className="btn btn-outline-primary rounded-3"
                            onClick={() => handleDownload("excel")}
                          >
                            <i className="ri-file-excel-2-line me-2"></i>{" "}
                            Download
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-info rounded-3"
                            onClick={() => handleDownload("print")}
                          >
                            <i className="ri-printer-line me-2"></i> Print
                          </button>
                        </div>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                          <thead>
                            <tr className="text-uppercase small text-muted">
                              <th className="ps-4 py-3 fw-semibold">#</th>
                              <th className="py-3 fw-semibold">Student</th>
                              <th className="py-3 fw-semibold">Phone</th>
                              <th className="py-3 fw-semibold">Fee Modules</th>
                              <th className="py-3 fw-semibold text-end">
                                Payable Fee
                              </th>
                              <th className="py-3 fw-semibold text-end">
                                Received
                              </th>
                              <th className="py-3 fw-semibold text-end">Due</th>
                              <th className="py-3 fw-semibold pe-4 text-end">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.length > 0 ? (
                              rows.map(({ student, totals }, index) => (
                                <tr key={student.id}>
                                  <td className="ps-4">{index + 1}</td>
                                  <td>
                                    <Link
                                      className="fw-semibold text-decoration-none"
                                      to={`/student/details?id=${student.id}`}
                                    >
                                      {student.name}{" "}
                                      <span className="text-muted fw-normal">
                                        ({student.registration_no})
                                      </span>
                                    </Link>
                                    <div className="text-muted small">
                                      {student.father_name}
                                    </div>
                                  </td>
                                  <td>{student.phone || "-"}</td>
                                  <td>
                                    {totals.breakdown.length > 0
                                      ? renderBreakdown(totals.breakdown)
                                      : "-"}
                                  </td>
                                  <td className="text-end text-nowrap">
                                    {inr(totals.finalFee)}
                                  </td>
                                  <td className="text-end text-nowrap fw-semibold text-success">
                                    {inr(totals.totalPaid)}
                                  </td>
                                  <td className="text-end text-nowrap fw-bold text-danger">
                                    {inr(totals.totalDue)}
                                  </td>
                                  <td className="pe-4 text-end">
                                    <div className="d-flex gap-2 justify-content-end">
                                      {hasAccess("/student/details") && (
                                        <Link
                                          className="btn btn-label-primary btn-sm"
                                          to={`/student/details?id=${student.id}`}
                                          title="View student"
                                        >
                                          <i className="ri-eye-line ri-20px"></i>
                                        </Link>
                                      )}
                                      {hasAccess("/student/fees/deposit") && (
                                        <Link
                                          className="btn btn-label-danger btn-sm"
                                          to={`/student/fees/deposit?student_id=${student.id}`}
                                          title="Pay Fee"
                                        >
                                          <i className="ri-hand-coin-line"></i>
                                        </Link>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan="8"
                                  className="text-center text-muted py-5"
                                >
                                  <i className="ri-inbox-line fs-2 d-block mb-2 text-secondary"></i>
                                  No Due Fee Records Found
                                </td>
                              </tr>
                            )}
                          </tbody>
                          {rows.length > 0 && (
                            <tfoot>
                              <tr style={{ background: "#f8f9fc" }}>
                                <td
                                  colSpan="4"
                                  className="ps-4 py-3 text-end fw-bold"
                                >
                                  Page Total
                                </td>
                                <td className="py-3 fw-bold text-end text-nowrap">
                                  {inr(summary.finalFee)}
                                </td>
                                <td className="py-3 fw-bold text-end text-nowrap text-success">
                                  {inr(summary.totalPaid)}
                                </td>
                                <td className="py-3 fw-bold text-end text-nowrap text-danger">
                                  {inr(summary.totalDue)}
                                </td>
                                <td className="pe-4"></td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
